import { supabase } from "./supabase";

// =============================================================================
// TYPES
// =============================================================================

export interface ChatMessage {
  id?: string;
  role: "user" | "assistant";
  content: string;
  timestamp?: string;
}

export interface ChatContext {
  currentStep?: number;
  stepTitle?: string;
  pageSection?: "theory" | "tutorial" | "practice";
  lastProblem?: string;
  userAnswer?: string;
  correctAnswer?: string;
}

export interface SendMessageRequest {
  techniqueId?: string; // Optional - for general platform questions
  message: string;
  conversationId?: string;
  context?: ChatContext;
  pageContext?: string; // e.g., "dashboard", "practice", "tournament", "arena"
}

export interface SendMessageResponse {
  success: boolean;
  reply?: string;
  conversationId?: string;
  remainingMessages?: number;
  error?: string;
}

export interface ChatConversation {
  id: string;
  techniqueId: string;
  title?: string;
  messageCount: number;
  createdAt: string;
  updatedAt: string;
}

// =============================================================================
// API FUNCTIONS
// =============================================================================

/**
 * Send a message to the technique chatbot
 */
export async function sendChatMessage(
  request: SendMessageRequest,
): Promise<SendMessageResponse> {
  try {
    const { data, error } = await supabase.functions.invoke(
      "technique-chatbot",
      {
        body: request,
      },
    );

    if (error) {
      console.error("Chat API error:", error);
      return {
        success: false,
        error: error.message || "Failed to send message",
      };
    }

    return data as SendMessageResponse;
  } catch (err: any) {
    console.error("Chat API exception:", err);
    return {
      success: false,
      error: err.message || "An unexpected error occurred",
    };
  }
}

/**
 * Get chat history for a conversation
 */
export async function getChatHistory(
  conversationId: string,
): Promise<ChatMessage[]> {
  try {
    const { data, error } = await supabase
      .from("technique_chat_messages")
      .select("id, role, content, created_at")
      .eq("conversation_id", conversationId)
      .order("created_at", { ascending: true });

    if (error) {
      console.error("Failed to fetch chat history:", error);
      return [];
    }

    return (data || []).map((msg) => ({
      id: msg.id,
      role: msg.role as "user" | "assistant",
      content: msg.content,
      timestamp: msg.created_at,
    }));
  } catch (err) {
    console.error("Failed to fetch chat history:", err);
    return [];
  }
}

/**
 * Get recent conversation for a technique (if any)
 */
export async function getRecentConversation(
  techniqueId: string,
): Promise<ChatConversation | null> {
  try {
    const { data, error } = await supabase
      .from("technique_chat_conversations")
      .select("id, technique_id, title, message_count, created_at, updated_at")
      .eq("technique_id", techniqueId)
      .order("updated_at", { ascending: false })
      .limit(1)
      .single();

    if (error || !data) {
      return null;
    }

    return {
      id: data.id,
      techniqueId: data.technique_id,
      title: data.title,
      messageCount: data.message_count,
      createdAt: data.created_at,
      updatedAt: data.updated_at,
    };
  } catch (err) {
    return null;
  }
}

/**
 * Get user's remaining daily messages
 */
export async function getRemainingMessages(): Promise<number | null> {
  try {
    const today = new Date().toISOString().split("T")[0];
    const { data, error } = await supabase
      .from("user_chat_usage")
      .select("messages_sent")
      .eq("date", today)
      .single();

    if (error || !data) {
      // No usage record = full quota available
      return 20; // Default free limit
    }

    // Assume free limit of 20 for now
    return Math.max(0, 20 - (data.messages_sent || 0));
  } catch (err) {
    return null;
  }
}
